import React from 'react';
import { motion } from 'framer-motion';
import { Home, ShieldCheck, MapPin, Users, Wifi, Utensils, Zap, Truck, Wrench, Sofa, ArrowRight } from 'lucide-react';
import { Button } from '../components/Button';
export function ServicesPage() {
  const services = [{
    title: 'Independent Living Housing',
    description: 'Our primary service provides high-quality, shared residential housing for adults who are able to live independently but benefit from a structured, community-focused environment.',
    img: 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&q=80&w=1000',
    features: ['Fully furnished rooms', 'All utilities included', 'High-speed Wi-Fi', 'Shared common areas']
  }, {
    title: 'Housing Placement',
    description: 'We specialize in rapid housing placement for individuals transitioning from hospitals, behavioral health centers, or other temporary living situations.',
    img: 'https://images.unsplash.com/photo-1573497620053-ea5300f94f21?auto=format&fit=crop&q=80&w=1000',
    features: ['24-48 hour response time', 'Streamlined intake process', 'Coordination with case managers', 'Transportation assistance']
  }, {
    title: 'Community Living Support',
    description: 'Our homes are managed to foster a sense of community and mutual support among residents, reducing isolation and promoting long-term stability.',
    img: 'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?auto=format&fit=crop&q=80&w=1000',
    features: ['House managers', 'Community meetings', 'Conflict resolution', 'Social activities']
  }];
  const amenities = [{
    icon: Wifi,
    title: 'High-Speed Wi-Fi',
    desc: 'Stay connected with complimentary high-speed internet in every home.'
  }, {
    icon: Utensils,
    title: 'Modern Kitchens',
    desc: 'Fully equipped kitchens with modern appliances for resident use.'
  }, {
    icon: Zap,
    title: 'Utilities Included',
    desc: 'No need to worry about bills—water, gas, and electric are all covered.'
  }, {
    icon: Truck,
    title: 'Transportation Access',
    desc: 'Homes are strategically located near public transit routes.'
  }, {
    icon: Wrench,
    title: '24/7 Maintenance',
    desc: 'Professional maintenance team to ensure everything stays in top shape.'
  }, {
    icon: Sofa,
    title: 'Fully Furnished',
    desc: 'Move in with just your personal items—we provide the rest.'
  }];
  return <div className="w-full" data-id="element-620">
      {/* Hero */}
      <section className="py-20 md:py-32 bg-background px-6" data-id="element-621">
        <div className="max-w-7xl mx-auto text-center" data-id="element-622">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} data-id="element-623">
            <h1 className="text-4xl md:text-5xl lg:text-7xl font-display font-extrabold text-charcoal mb-6 md:mb-8" data-id="element-624">
              Our Services
            </h1>
            <p className="text-lg md:text-xl text-muted max-w-3xl mx-auto leading-relaxed" data-id="element-625">
              We provide more than just a roof over your head. We provide the
              foundation for a stable, independent life through comprehensive
              housing and support services.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Service Blocks */}
      <section className="py-16 md:py-20 px-6" data-id="element-626">
        <div className="max-w-7xl mx-auto space-y-20 md:space-y-32" data-id="element-627">
          {services.map((service, idx) => <div key={service.title} className={`flex flex-col lg:items-center gap-10 md:gap-16 ${idx % 2 !== 0 ? 'lg:flex-row-reverse' : 'lg:flex-row'}`} data-id="element-628">
              <div className="lg:w-1/2" data-id="element-629">
                <div className="rounded-3xl overflow-hidden shadow-2xl" data-id="element-630">
                  <img src={service.img} alt={service.title} className="w-full aspect-[4/3] object-cover" data-id="element-631" />
                </div>
              </div>
              <div className="lg:w-1/2 space-y-8" data-id="element-632">
                <h2 className="text-4xl font-display font-bold text-charcoal" data-id="element-633">
                  {service.title}
                </h2>
                <p className="text-lg text-muted leading-relaxed" data-id="element-634">
                  {service.description}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-635">
                  {service.features.map(feature => <div key={feature} className="flex items-center space-x-3" data-id="element-636">
                      <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-primary" data-id="element-637">
                        <ArrowRight size={14} data-id="element-638" />
                      </div>
                      <span className="font-semibold text-charcoal" data-id="element-639">
                        {feature}
                      </span>
                    </div>)}
                </div>
                <Button size="lg" to="/contact" data-id="element-640">
                  Inquire About This Service
                </Button>
              </div>
            </div>)}
        </div>
      </section>

      {/* Amenities Grid */}
      <section className="py-20 md:py-32 bg-primary px-6" data-id="element-641">
        <div className="max-w-7xl mx-auto" data-id="element-642">
          <div className="text-center mb-12 md:mb-20" data-id="element-643">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-white mb-4 md:mb-6" data-id="element-644">
              Home Amenities
            </h2>
            <p className="text-white/70 text-lg md:text-xl max-w-2xl mx-auto" data-id="element-645">
              Every Birds Nest home is designed for comfort, convenience, and
              community.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-646">
            {amenities.map(amenity => <div key={amenity.title} className="bg-white/10 backdrop-blur-md border border-white/10 p-10 rounded-2xl hover:bg-white/20 transition-colors" data-id="element-647">
                <div className="w-14 h-14 bg-accent rounded-xl flex items-center justify-center text-charcoal mb-8" data-id="element-648">
                  <amenity.icon size={28} data-id="element-649" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4" data-id="element-650">
                  {amenity.title}
                </h3>
                <p className="text-white/60 leading-relaxed" data-id="element-651">{amenity.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 md:py-32 px-6 text-center" data-id="element-652">
        <div className="max-w-3xl mx-auto space-y-6 md:space-y-8" data-id="element-653">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-charcoal" data-id="element-654">
            Need Housing Today?
          </h2>
          <p className="text-lg md:text-xl text-muted" data-id="element-655">
            We have beds available across multiple locations in Maryland.
            Contact our admissions team for immediate assistance.
          </p>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-656">
            <Button size="lg" to="/homes" data-id="element-657">
              Check Availability
            </Button>
            <Button variant="outline" size="lg" href="tel:+14105550123" data-id="element-658">
              Call (410) 555-0123
            </Button>
          </div>
        </div>
      </section>
    </div>;
}